// One pairing of US state code to state name, because the app needs both and
// they are not interchangeable.
//
//   account.billingstate / shippingstate  -> the CODE. stateOptions binds "FL",
//       and the zip autofill returns "FL" (uszipcode's `state` field), so the
//       select can only display a code. 561,549 accounts hold one.
//
//   boat.home_port_state__c               -> the NAME. Home_Port_State__c is a
//       RESTRICTED picklist in Salesforce holding "Florida"; not one of the
//       656,840 synced boats holds a code, and sending one is rejected outright
//       ("Home Port State: bad value for restricted picklist field: MS").
//
// Getting it backwards is not a display nit on the boat side - it is the
// rejection that stranded boats in Postgres.
import { STATES_BY_COUNTRY } from "./homePort";

export const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District Of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "PR", name: "Puerto Rico" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },

  // In the Home Port State picklist but with no entry in the address list,
  // so an address typed as a territory had no code to fold onto.
  { code: "AS", name: "American Samoa" },
  { code: "FM", name: "Federated Micronesia" },
  { code: "GU", name: "Guam" },
  { code: "MH", name: "Marshall Islands" },
  { code: "MP", name: "Northern Mariana Islands" },
  { code: "PW", name: "Palau" },
  { code: "VI", name: "US Virgin Islands" },
  { code: "UM", name: "United States Minor Outlying Islands" },
  { code: "AA", name: "Armed Forces Americas" },
  { code: "AE", name: "Armed Forces Europe" },
  { code: "AP", name: "Armed Forces Pacific" },
];

const BY_KEY = US_STATES.reduce((acc, entry) => {
  acc[entry.code.toLowerCase()] = entry;
  acc[entry.name.toLowerCase()] = entry;
  return acc;
}, {});

function lookup(value) {
  if (value === null || value === undefined) return null;
  const key = String(value).trim().toLowerCase();
  return key ? BY_KEY[key] || null : null;
}

// "Florida", "florida", "fl" -> "FL". Anything unrecognized comes back
// untouched: a state we cannot place is data, not a typo to overwrite.
export function toStateCode(value) {
  const entry = lookup(value);
  return entry ? entry.code : value;
}

// "FL", "fl", "florida" -> "Florida", but only ever a string the Salesforce
// picklist actually contains. The picklist is the authority on spelling - it
// holds "District of Columbia" where the address list says "District Of
// Columbia" - so the answer is read back out of it rather than from here.
export function toHomePortState(value) {
  const entry = lookup(value);
  if (!entry) return value;
  const allowed = STATES_BY_COUNTRY["United States"] || [];
  const match = allowed.find(
    (state) => state.toLowerCase() === entry.name.toLowerCase()
  );
  return match || value;
}
